import React from 'react';
import { HorseFilters, Country } from '../types/api';
import { X } from 'lucide-react';

interface FilterChipsProps {
  filters: HorseFilters;
  onRemoveFilter: (filterKey: keyof HorseFilters) => void;
  onClearAll: () => void;
  countries?: Country[];
}

export const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
  countries = [],
}) => {
  const countryNameMap = React.useMemo(
    () => new Map(countries.map(c => [c.code, c.name])),
    [countries]
  );
  const activeFilters = Object.entries(filters).filter(([key, value]) =>
    value !== undefined && value !== null && value !== '' && key !== 'last_result_unit'
  );

  if (activeFilters.length === 0) {
    return null;
  }

  const getFilterLabel = (key: string, value: any): string => {
    switch (key) {
      case 'name':
        return `Name: ${value}`;
      case 'birth_year_min':
        return `Birth Year ≥ ${value}`;
      case 'birth_year_max':
        return `Birth Year ≤ ${value}`;
      case 'show_level_min':
        return `Show Level ≥ ${value}cm`;
      case 'show_level_max':
        return `Show Level ≤ ${value}cm`;
      case 'sex':
        return `Sex: ${(value as string[]).join(', ')}`;
      case 'countries':
        return `Country: ${(value as string[]).map(code => countryNameMap.get(code) ?? code).join(', ')}`;
      case 'in_collection':
        return value ? 'In Collection' : 'Not in Collection';
      case 'is_private':
        return value ? 'Private Horses Only' : 'Not Private';
      case 'is_pony':
        return value ? 'Pony' : 'Horse';
      case 'has_videos':
        return 'Has Videos';
      case 'last_result_within':
        return `Active within ${value} ${filters.last_result_unit || 'months'}`;
      default:
        return `${key}: ${value}`;
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">
          Active Filters ({activeFilters.length})
        </h3>
        <button
          onClick={onClearAll}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {activeFilters.map(([key, value]) => (
          <div
            key={key}
            className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
          >
            <span>{getFilterLabel(key, value)}</span>
            <button
              onClick={() => onRemoveFilter(key as keyof HorseFilters)}
              className="ml-2 text-blue-600 hover:text-blue-800"
              title={`Remove ${key} filter`}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}; 